import type { ApiError, ErrorCode, FieldError } from '@/contracts/common.contract'

/**
 * 前端統一的錯誤型別。
 *
 * 不論失敗來自後端的錯誤信封、網路中斷、還是非預期的例外，
 * 一律正規化成這個形狀 —— 上層只需要處理一種錯誤，
 * 而且永遠拿得到一個可以查 i18n 的 `code`。
 */
export class ApiRequestError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR' | 'UNKNOWN'
  readonly fields: readonly FieldError[]
  readonly status: number | null

  constructor(params: {
    code: ErrorCode | 'NETWORK_ERROR' | 'UNKNOWN'
    message: string
    fields?: readonly FieldError[]
    status?: number | null
  }) {
    super(params.message)
    this.name = 'ApiRequestError'
    this.code = params.code
    this.fields = params.fields ?? []
    this.status = params.status ?? null
  }

  /** i18n 查表用的 key，對應 `locales/*.json` 的 `errors` 區塊 */
  get i18nKey(): string {
    return `errors.${this.code}`
  }

  /** 把欄位錯誤轉成 `{ 欄位: 訊息 key }`，供表單標紅 */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.fields.map((f) => [f.field, `validation.${f.code}`]))
  }
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiError).code === 'string' &&
    typeof (value as ApiError).message === 'string'
  )
}

/** 把任何丟出來的東西轉成 `ApiRequestError` */
export function normalizeError(error: unknown): ApiRequestError {
  if (error instanceof ApiRequestError) return error

  const response = (error as { response?: { status?: number; data?: unknown } }).response

  if (response !== undefined) {
    const payload = response.data as { error?: unknown } | undefined

    if (payload !== undefined && isApiError(payload.error)) {
      return new ApiRequestError({
        code: payload.error.code,
        message: payload.error.message,
        fields: payload.error.fields ?? [],
        status: response.status ?? null,
      })
    }

    return new ApiRequestError({
      code: 'UNKNOWN',
      message: `Request failed with status ${String(response.status)}`,
      status: response.status ?? null,
    })
  }

  // 沒有 response 代表請求根本沒送達（斷網、CORS、逾時）
  return new ApiRequestError({
    code: 'NETWORK_ERROR',
    message: error instanceof Error ? error.message : 'Network request failed',
  })
}
